import React, { useState } from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import VacationRequestSummary from "../../components/VacationRequestSummary.molecule"
import Divider from "../../../../components/atoms/Divider.atom"
import GridContainer from "../../../../components/layouts/containers/GridContainer.container"
import GridItem from "../../../../components/layouts/containers/GridItem.container"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import {
  getVacationState,
  isForeignSchema,
  isVacationValidForPlusDaysPayDate,
} from "../../../../utils/vacation.utils"
import VacationStatusChip from "../../../../components/molecules/VacationStatusChip.molecule"
import UnifiedDatePicker from "../../../../components/compounds/UnifiedDatePicker.compound"
import { DateTime } from "luxon"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import DangerButton from "../../../../components/molecules/buttons/DangerButton.molecule"
import ModalFormCancelApprovedVacation from "./ModalFormCancelApprovedVacation.controller"
import { updateApprovedVacationPlusDaysPayDate } from "../../../../services/vacation.service"
import useToast from "../../../../hooks/useToast.hook"
import { isLessThanDate } from "../../../../utils/date.utils"
import {
  VACATIONS_SAVE_ERROR_MESSAGE,
  VACATIONS_SAVE_SUCCESS_MESSAGE,
} from "../../../../consts/modules/vacations.consts"
import { useSession } from "../../../../hooks/auth/useSession.hook"

const ModalFormApprovedRequestReview = ({
  visible,
  setVisible,
  selectedVacationRequest,
  triggerRefresh,
  className = "",
}) => {
  const { user } = useSession()
  const thisVacationWasCreatedByMe =
    selectedVacationRequest?.createdBy === user?.id
  const [cancelApprovedModalVisible, setCancelApprovedModalVisible] =
    useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [plusDaysPayDate, setPlusDaysPayDate] = useState(
    selectedVacationRequest?.plusDaysPayDate
  )
  const { setSuccessMessage, setErrorMessage } = useToast()
  const renderPlusDaysPayDateTemplate = (vacationRequest) => {
    if (isForeignSchema(vacationRequest?.teamMember.idHolidaySchema)) {
      return null
    }
    if (!isVacationValidForPlusDaysPayDate(selectedVacationRequest)) {
      return null
    }
    if (!selectedVacationRequest.plusDaysPayDate) {
      return (
        <GridItem col={6}>
          <ValueWithLabelTemplate
            label="Pago de Días+"
            value={
              <UnifiedDatePicker
                className="w-full"
                dateRange={[plusDaysPayDate, plusDaysPayDate]}
                setDateRange={(e) => {
                  setPlusDaysPayDate(e[0])
                }}
                datePickerProps={{
                  className: "w-full",
                  autoFocus: true,
                }}
              />
            }
          />
        </GridItem>
      )
    } else {
      return (
        <GridItem col={6}>
          <ValueWithLabelTemplate
            label="Pago de Días+"
            value={DateTime.fromISO(selectedVacationRequest.plusDaysPayDate, {
              zone: "utc",
            }).toFormat("dd/MM/yyyy")}
          />
        </GridItem>
      )
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const response = await updateApprovedVacationPlusDaysPayDate(
        selectedVacationRequest.idVacations,
        plusDaysPayDate
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      resetFormState()
      triggerRefresh()
      setVisible(false)
      setSuccessMessage(VACATIONS_SAVE_SUCCESS_MESSAGE)
    } catch (error) {
      console.error(error)
      setErrorMessage(VACATIONS_SAVE_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  const modalFooterTemplate = () => {
    const showDangerButton =
      !isVacationValidForPlusDaysPayDate(selectedVacationRequest) ||
      (isVacationValidForPlusDaysPayDate(selectedVacationRequest) &&
        !selectedVacationRequest.plusDaysPayDate) ||
      !isLessThanDate(selectedVacationRequest?.plusDaysPayDate, new Date()) ||
      isForeignSchema(selectedVacationRequest.teamMember.idHolidaySchema)
    return (
      <div>
        {showDangerButton && (
          <DangerButton
            label="Cancelar aprobación"
            btnType="outlined"
            icon={`pi pi-ban`}
            onClick={() => {
              setCancelApprovedModalVisible(true)
              setVisible(false)
            }}
            rounded
            disabled={submitting}
          />
        )}
        {isVacationValidForPlusDaysPayDate(selectedVacationRequest) &&
          !selectedVacationRequest.plusDaysPayDate && (
            <PrimaryButton
              rounded
              icon={`pi ${submitting ? "pi-spin pi-spinner" : "pi-check"}`}
              label={"Guardar"}
              onClick={handleSubmit}
              disabled={
                submitting ||
                (!isForeignSchema(
                  selectedVacationRequest.teamMember.idHolidaySchema
                ) &&
                  !plusDaysPayDate)
              }
            />
          )}
      </div>
    )
  }
  const resetFormState = () => {
    setPlusDaysPayDate(null)
  }

  const isWithoutFooter = () => {
    const isForeign = isForeignSchema(
      selectedVacationRequest.teamMember.idHolidaySchema
    )
    const isValidForPayDate = isVacationValidForPlusDaysPayDate(
      selectedVacationRequest
    )
    const payDate = selectedVacationRequest?.plusDaysPayDate
    if (!isValidForPayDate || isForeign) return false
    if (payDate && isLessThanDate(payDate, new Date())) return true
    return false
  }

  if (!selectedVacationRequest) {
    return null
  }
  return (
    <>
      <ModalForm
        className={`vacation-approved-review-modal-form ${thisVacationWasCreatedByMe && user.isAdmin() ? "hide-actions-footer" : ""} ${className}`}
        headerTitle="Solicitud de vacaciones"
        footer={modalFooterTemplate()}
        withoutFooter={isWithoutFooter()}
        headerIcon={
          <BeachAccessOutlinedIcon
            style={{ color: "var(--clr-info__dark)", fontSize: "32px" }}
          />
        }
        visible={visible}
        setVisible={setVisible}
        dismissableMask
        closable
        cleanUp={resetFormState}
      >
        <VacationRequestSummary vacationRequest={selectedVacationRequest} />
        <Divider stroke="sm" />
        <GridContainer className="state-form">
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Estado"
              value={
                <VacationStatusChip
                  status={getVacationState(selectedVacationRequest)}
                  variant="long"
                />
              }
            />
          </GridItem>
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Responsable"
              value={selectedVacationRequest?.approver?.fullName}
            />
          </GridItem>
          {renderPlusDaysPayDateTemplate(selectedVacationRequest)}
        </GridContainer>
      </ModalForm>
      <ModalFormCancelApprovedVacation
        visible={cancelApprovedModalVisible}
        setVisible={setCancelApprovedModalVisible}
        setParentVisible={setVisible}
        vacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
    </>
  )
}

export default ModalFormApprovedRequestReview

ModalFormApprovedRequestReview.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selectedVacationRequest: PropTypes.object,
  triggerRefresh: PropTypes.func,
  className: PropTypes.string,
}
